import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Sparkles, Copy, Check, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminContent = () => {
  const [universes, setUniverses] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUniverse, setSelectedUniverse] = useState("");
  const [section, setSection] = useState("description");
  const [context, setContext] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [savedItems, setSavedItems] = useState([]);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const response = await axios.get(`${API}/universes`);
        setUniverses(response.data);
      } catch (error) {
        toast.error("Failed to fetch universes");
      }
    };
    fetchUniverses();
  }, []);

  const handleGenerate = async () => {
    if (!selectedUniverse) {
      toast.error("Please select a universe");
      return;
    }

    const universe = universes.find(u => u.id === selectedUniverse);
    if (!universe) return;

    setGenerating(true);
    setGeneratedContent("");

    try {
      const response = await axios.post(`${API}/ai/generate`, {
        universe_name: universe.name,
        section: section,
        context: context || null,
      });
      setGeneratedContent(response.data.content);
      toast.success("Content generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAndSave = async () => {
    if (!selectedUniverse) {
      toast.error("Please select a universe");
      return;
    }

    const universe = universes.find(u => u.id === selectedUniverse);
    if (!universe) return;

    if (section === "description") {
      toast.error("Use 'Generate' for descriptions. 'Generate & Save' works for characters, items, games, and music.");
      return;
    }

    setSaving(true);
    setGeneratedContent("");

    try {
      const response = await axios.post(`${API}/ai/generate-and-save`, {
        universe_id: universe.id,
        universe_name: universe.name,
        section: section,
        context: context || null,
      });

      setGeneratedContent(response.data.content);

      if (response.data.saved) {
        toast.success(`Content generated and saved as a new ${section}!`);
        setSavedItems(prev => [response.data.saved_item, ...prev]);
      } else {
        toast.info("Content generated but could not be auto-saved. You can copy and use it manually.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const sections = [
    { value: "description", label: "Universe Description", saveable: false },
    { value: "character", label: "Character Profile", saveable: true },
    { value: "weapon", label: "Weapon / Artifact / Creature", saveable: true },
    { value: "game", label: "Game", saveable: true },
    { value: "music", label: "Music / Soundtrack", saveable: true },
  ];

  const currentSection = sections.find(s => s.value === section);

  return (
    <AdminLayout title="AI Content Generator">
      <div className="max-w-4xl">
        <div className="card-surface p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-['Outfit']">Generate Lore Content</h2>
              <p className="text-sm text-slate-400">Use AI to create descriptions, character bios, weapons, games, and more</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Universe</Label>
                <Select value={selectedUniverse} onValueChange={setSelectedUniverse}>
                  <SelectTrigger className="input-dark mt-1" data-testid="ai-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {universes.map((uni) => (
                      <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Content Type</Label>
                <Select value={section} onValueChange={setSection}>
                  <SelectTrigger className="input-dark mt-1" data-testid="ai-section">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {sections.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                        {s.saveable && <span className="text-green-500 ml-1 text-xs">(auto-save)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Additional Context (optional)</Label>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="input-dark mt-1"
                placeholder="E.g., 'Create a villain with fire powers' or 'A legendary sword from ancient times'"
                data-testid="ai-context"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleGenerate}
                disabled={generating || saving || !selectedUniverse}
                className="btn-primary flex items-center gap-2 flex-1 py-3"
                data-testid="ai-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? "Generating..." : "Generate"}
              </Button>
              
              {currentSection?.saveable && (
                <Button
                  onClick={handleGenerateAndSave}
                  disabled={generating || saving || !selectedUniverse}
                  className="flex items-center gap-2 flex-1 py-3 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="ai-generate-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Generating & Saving..." : "Generate & Save"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Generated Content */}
        {generatedContent && (
          <div className="card-surface p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white font-['Outfit']">Generated Content</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyToClipboard}
                className="flex items-center gap-2"
                data-testid="copy-content-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <div className="bg-[#030712] p-4 rounded border border-white/5">
              <p className="text-slate-300 whitespace-pre-line leading-relaxed" data-testid="generated-content">
                {generatedContent}
              </p>
            </div>
          </div>
        )}

        {/* Recently Saved Items */}
        {savedItems.length > 0 && (
          <div className="card-surface p-6">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Recently Saved Items</h3>
            <div className="space-y-3">
              {savedItems.map((item) => (
                <div key={item.id} className="bg-[#030712] p-4 rounded border border-green-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-mono uppercase tracking-wider text-green-500">
                        {item.species ? "Character" : item.type || "Item"}
                      </span>
                      <h4 className="text-white font-semibold mt-1">{item.name}</h4>
                    </div>
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Saved</span>
                  </div>
                  <p className="text-slate-400 text-sm mt-2 line-clamp-2">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
